import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { REGISTER_URL } from '../lib/authLinks';

const AboutPage = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Header />
      
      <main className="pt-24 pb-20">
        <div className="max-w-7xl mx-auto px-6">
          {/* Hero Section */}
          <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <h1 className="text-5xl md:text-6xl font-bold mb-6 animate-fade-in-down">
              About <span className="text-yellow-500">SpreadexAsia</span>
            </h1>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto animate-fade-in-up animate-delay-200">
              AI-powered automated trading for Forex and Stock markets since 2018
            </p>
          </div>

          {/* Who We Are */}
          <section className="mb-20 animate-fade-in-up animate-delay-300">
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 md:p-12 border border-gray-800 hover-lift hover-glow">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-yellow-500 animate-fade-in-left">Who We Are</h2>
              <p className="text-lg text-gray-300 leading-relaxed animate-fade-in-right animate-delay-200">
                Founded in 2018, SpreadexAsia is an innovative platform specializing in AI-powered automated trading for Forex and Stock markets. With over 7 years of hands-on experience in financial markets and trading technology, we are committed to providing tools that help traders make informed decisions while navigating market risks intelligently.
              </p>
            </div>
          </section>

          {/* Our Mission */}
          <section className="mb-20 animate-fade-in-up animate-delay-400">
            <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 md:p-12 border border-gray-800 hover-lift hover-glow">
              <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-6 animate-float">
                <span className="text-3xl">🎯</span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-yellow-500 animate-fade-in-left">Our Mission</h2>
              <p className="text-lg text-gray-300 leading-relaxed animate-fade-in-right animate-delay-200">
                Our mission is simple: to make trading smarter, safer, and more accessible. By combining advanced AI technology, real-time market analysis, and disciplined risk-management strategies, SpreadexAsia empowers traders—both beginners and experienced investors—to engage in markets with clarity and confidence.
              </p>
            </div>
          </section>

          {/* What We Offer */}
          <section className="mb-20 animate-fade-in-up animate-delay-500">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 animate-scale-in">What We <span className="text-yellow-500">Offer</span></h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 border border-gray-800 hover-lift hover-glow animate-fade-in-left">
                <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-6 animate-float">
                  <span className="text-3xl">🤖</span>
                </div>
                <h3 className="text-2xl font-bold mb-4">24/7 AI Trading</h3>
                <p className="text-gray-300">
                  Our algorithms scan multiple markets continuously, executing trades automatically.
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 border border-gray-800 hover-lift hover-glow animate-fade-in-right animate-delay-100">
                <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-6 animate-float">
                  <span className="text-3xl">🛡️</span>
                </div>
                <h3 className="text-2xl font-bold mb-4">Risk-Managed Strategies</h3>
                <p className="text-gray-300">
                  The system uses data-driven techniques to help manage exposure and improve decision-making.
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 border border-gray-800 hover-lift hover-glow animate-fade-in-left animate-delay-200">
                <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-6 animate-float">
                  <span className="text-3xl">✨</span>
                </div>
                <h3 className="text-2xl font-bold mb-4">Beginner-Friendly Platform</h3>
                <p className="text-gray-300">
                  No prior trading knowledge is required—our platform handles the complexity for you.
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-8 border border-gray-800 hover-lift hover-glow animate-fade-in-right animate-delay-300">
                <div className="w-16 h-16 bg-yellow-500/20 rounded-full flex items-center justify-center mb-6 animate-float">
                  <span className="text-3xl">📊</span>
                </div>
                <h3 className="text-2xl font-bold mb-4">Transparency</h3>
                <p className="text-gray-300">
                  Users can monitor performance, deposits, and withdrawals in real-time.
                </p>
              </div>
            </div>
          </section>

          {/* Our Philosophy */}
          <section className="mb-20 animate-fade-in-up animate-delay-600">
            <div className="bg-gradient-to-r from-yellow-900/30 to-gray-900 rounded-2xl p-8 md:p-12 border border-yellow-500/30 hover-glow animate-pulse">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center animate-scale-in">Our <span className="text-yellow-500">Philosophy</span></h2>
              <p className="text-lg text-gray-300 leading-relaxed text-center max-w-4xl mx-auto animate-fade-in animate-delay-200">
                Trading is inherently risky, but we believe technology can make it smarter. At SpreadexAsia, we focus on responsible trading practices, transparency, and innovation. Our goal is to reduce uncertainty, provide insight, and give traders the tools they need to pursue opportunities confidently.
              </p>
            </div>
          </section>

          {/* Why Choose SpreadexAsia */}
          <section className="mb-20">
            <h2 className="text-3xl md:text-4xl font-bold text-center mb-12 animate-scale-in">Why Choose <span className="text-yellow-500">SpreadexAsia?</span></h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-6 text-center border border-gray-800 hover-lift hover-glow animate-fade-in-up">
                <div className="text-4xl mb-4 animate-float">📅</div>
                <h3 className="text-xl font-bold mb-2">7 Years Experience</h3>
                <p className="text-gray-400 text-sm">Real-world experience in Forex & Stock trading</p>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-6 text-center border border-gray-800 hover-lift hover-glow animate-fade-in-up animate-delay-100">
                <div className="text-4xl mb-4 animate-float">🤖</div>
                <h3 className="text-xl font-bold mb-2">AI-Driven</h3>
                <p className="text-gray-400 text-sm">Automation for hands-free trading</p>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-6 text-center border border-gray-800 hover-lift hover-glow animate-fade-in-up animate-delay-200">
                <div className="text-4xl mb-4 animate-float">🔒</div>
                <h3 className="text-xl font-bold mb-2">Secure Platform</h3>
                <p className="text-gray-400 text-sm">Transparent and user-friendly</p>
              </div>

              <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-6 text-center border border-gray-800 hover-lift hover-glow animate-fade-in-up animate-delay-300">
                <div className="text-4xl mb-4 animate-float">💬</div>
                <h3 className="text-xl font-bold mb-2">Continuous Support</h3>
                <p className="text-gray-400 text-sm">Updates to improve trading experience</p>
              </div>
            </div>
          </section>

          {/* Join Us CTA */}
          <section className="bg-gradient-to-br from-gray-900 to-black rounded-2xl p-12 text-center border border-gray-800 hover-glow animate-scale-in">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 animate-fade-in-down">Join <span className="text-yellow-500">Us</span></h2>
            <p className="text-lg text-gray-300 leading-relaxed max-w-3xl mx-auto mb-8 animate-fade-in-up animate-delay-200">
              SpreadexAsia is more than a trading platform; it's a partner for smarter financial decisions. Whether you are new to trading or looking to automate your strategies, SpreadexAsia provides the tools, guidance, and technology to help you navigate the markets responsibly and efficiently.
            </p>
            <a
              href={REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-yellow-500 hover:bg-yellow-600 text-black font-bold px-8 py-4 rounded-xl transition-all duration-300 text-lg hover-scale animate-glow"
            >
              Start with $100
            </a>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AboutPage;
